
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingSalesInvoiceService from "../services/tradingSalesInvoiceService";
import tradingEnquiryService from "../../services/tradingEnquiryService";
import tradingSalesOrderService from "../../services/tradingSalesOrderService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  "Unable to process Sales Invoice.";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = {
  page: {
    padding: "24px 28px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#243047",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 22,
  },
  title: {
    margin: 0,
    fontSize: 23,
    fontWeight: 750,
    color: "#172554",
  },
  subtitle: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 5,
  },
  card: {
    background: "#fff",
    border: "1px solid #e4e8f1",
    borderRadius: 11,
    padding: 20,
    marginBottom: 18,
    boxShadow: "0 2px 8px rgba(20,35,70,0.035)",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 750,
    color: "#172554",
    margin: "0 0 5px",
  },
  muted: {
    fontSize: 12,
    color: "#718096",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 650,
    color: "#475569",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "9px 11px",
    border: "1px solid #d8deea",
    borderRadius: 7,
    fontSize: 13,
    boxSizing: "border-box",
    background: "#fff",
  },
  primaryButton: {
    background: "linear-gradient(120deg,#6254df,#4936c9)",
    border: 0,
    borderRadius: 8,
    color: "#fff",
    padding: "10px 18px",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    background: "#fff",
    border: "1px solid #d8deea",
    borderRadius: 8,
    color: "#334155",
    padding: "9px 15px",
    fontWeight: 650,
    cursor: "pointer",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e5e9f2",
    borderRadius: 8,
  },
  table: {
    width: "100%",
    minWidth: 850,
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    background: "#f3f5fa",
    color: "#475569",
    textAlign: "left",
    padding: "12px 10px",
    fontSize: 10,
    fontWeight: 750,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "11px 10px",
    borderBottom: "1px solid #edf0f5",
    verticalAlign: "middle",
  },
  error: {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 13,
  },
  success: {
    background: "#ecfdf5",
    color: "#047857",
    border: "1px solid #a7f3d0",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 13,
  },
};

function SummaryCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e9f2",
        borderLeft: `4px solid ${color}`,
        borderRadius: 9,
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 650 }}>
        {label}
      </div>
      <div style={{ fontSize: 21, fontWeight: 750, marginTop: 7 }}>
        {value}
      </div>
    </div>
  );
}

export default function TradingSalesInvoiceCreatePage() {
  const navigate = useNavigate();

  const [challans, setChallans] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);

  const [selectedChallanIds, setSelectedChallanIds] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
const [customers, setCustomers] = useState([]);
const [selectedCustomer, setSelectedCustomer] = useState(null);

const [salesOrders, setSalesOrders] = useState([]);
const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);

const [loadingCustomers, setLoadingCustomers] = useState(false);
const [loadingSalesOrders, setLoadingSalesOrders] = useState(false);

  const [charges, setCharges] = useState([]);
  const [chargeTaxType, setChargeTaxType] = useState("NONE");

  const [search, setSearch] = useState("");
  const [step, setStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const searchCustomerList = async (searchText) => {
  setCustomerSearch(searchText);

  if (!searchText.trim()) {
    setCustomers([]);
    return;
  }

  setLoadingCustomers(true);
  setError("");

  try {
    const response =
      await tradingEnquiryService.searchCustomers(searchText);

    const body = getResponseData(response);

    setCustomers(
      Array.isArray(body.items)
        ? body.items
        : Array.isArray(body)
        ? body
        : []
    );
  } catch (err) {
    setError(getErrorMessage(err));
  } finally {
    setLoadingCustomers(false);
  }
};

const loadSalesOrders = async (customer) => {
  setSelectedCustomer(customer);
  setSelectedSalesOrder(null);
  setSalesOrders([]);
  setSelectedChallanIds([]);
  setChallans([]);

  if (!customer) return;

  setLoadingSalesOrders(true);
  setError("");

  try {
    const customerId =
      customer.business_partner_id ?? customer.id;

    if (!customerId) {
      setError("Selected customer does not have a valid Business Partner ID.");
      return;
    }

    const response =
      await tradingSalesOrderService.getSalesOrders({
        business_partner_id: customerId,
        per_page: 100,
      });

    const body = getResponseData(response);

    setSalesOrders(
      Array.isArray(body.items) ? body.items : []
    );
  } catch (err) {
    setError(getErrorMessage(err));
  } finally {
    setLoadingSalesOrders(false);
  }
};
  
  const selectSalesOrder = async (salesOrder) => {
  setSelectedSalesOrder(salesOrder);
  setSelectedChallanIds([]);
  setChallans([]);
  setAvailableItems([]);
  setInvoiceItems([]);
  setError("");

  if (!salesOrder || !selectedCustomer) return;

  setLoading(true);

  try {
    const customerId =
      selectedCustomer.business_partner_id ?? selectedCustomer.id;

    const response =
      await tradingSalesInvoiceService.getAvailableDeliveryChallans({
        customer_id: customerId,
        sales_order_id: salesOrder.id,
      });

      const salesOrderResponse =
  await tradingSalesOrderService.getSalesOrder(salesOrder.id);

const salesOrderData = getResponseData(salesOrderResponse);

const customerCharges = (
  Array.isArray(salesOrderData.charges)
    ? salesOrderData.charges
    : []
)
  .filter((charge) => charge.payer_type === "CUSTOMER")
  .map((charge) => ({
    charge_type: charge.charge_type || "",
    description: charge.remarks || "",
    charge_amount: String(charge.charge_amount ?? 0),
    gst_rate: String(charge.gst_rate ?? 0),
    remarks: charge.remarks || "",
  }));

setCharges(customerCharges);
    
      const body = getResponseData(response);


    setChallans(
      Array.isArray(body.items) ? body.items : []
    );
  } catch (err) {
    setError(getErrorMessage(err));
  } finally {
    setLoading(false);
  }
};

  const loadAvailableItems = async () => {
    setLoading(true);
    setError("");

    try {
      const customerId =
        selectedCustomer.business_partner_id ?? selectedCustomer.id;

      const response =
        await tradingSalesInvoiceService.getAvailableDeliveryChallanItems({
          customer_id: customerId,
          sales_order_id: selectedSalesOrder.id,
        });

      const body = getResponseData(response);

      const rows = Array.isArray(body.items) ? body.items : [];

      setAvailableItems(rows);

      const selectedRows = rows
        .filter((item) =>
          selectedChallanIds.includes(
            Number(item.delivery_challan_id)
          )
        )
        .map((item) => ({
          ...item,
          invoice_quantity: String(
            item.available_to_invoice_quantity ?? ""
          ),
        }));

      setInvoiceItems(selectedRows);
      setStep(2);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredChallans = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return challans;

    return challans.filter((row) =>
      [
        row.challan_no,
        row.customer_name,
        row.sales_order_id,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [challans, search]);

  const selectedChallans = useMemo(
    () =>
      challans.filter((row) =>
        selectedChallanIds.includes(
          Number(row.delivery_challan_id)
        )
      ),
    [challans, selectedChallanIds]
  );

  const selectedCustomerNames = [
    ...new Set(
      selectedChallans.map((row) => row.customer_name).filter(Boolean)
    ),
  ];

  const toggleChallan = (challanId) => {
    setSelectedChallanIds((previous) => {
      const id = Number(challanId);

      return previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id];
    });
  };

  const updateQuantity = (itemId, value) => {
    setInvoiceItems((previous) =>
      previous.map((item) =>
        Number(item.delivery_challan_item_id) === Number(itemId)
          ? { ...item, invoice_quantity: value }
          : item
      )
    );
  };

  const addCharge = () => {
    setCharges((previous) => [
      ...previous,
      {
        charge_type: "",
        description: "",
        charge_amount: "",
        gst_rate: "0",
        remarks: "",
      },
    ]);
  };

  const updateCharge = (index, field, value) => {
    setCharges((previous) =>
      previous.map((charge, i) =>
        i === index ? { ...charge, [field]: value } : charge
      )
    );
  };

  const removeCharge = (index) => {
    setCharges((previous) =>
      previous.filter((_, i) => i !== index)
    );
  };

  const quantitySummary = useMemo(() => {
    return invoiceItems.reduce(
      (result, item) => {
        result.challan += Number(item.challan_quantity || 0);
        result.available += Number(
          item.available_to_invoice_quantity || 0
        );
        result.invoice += Number(item.invoice_quantity || 0);
        return result;
      },
      { challan: 0, available: 0, invoice: 0 }
    );
  }, [invoiceItems]);

  const chargeSummary = useMemo(() => {
    return charges.reduce(
      (result, charge) => {
        const amount = Number(charge.charge_amount || 0);
        const rate = Number(charge.gst_rate || 0);

        result.amount += amount;
        result.gst += amount * rate / 100;

        return result;
      },
      { amount: 0, gst: 0 }
    );
  }, [charges]);

  const validateItems = () => {
    if (!invoiceItems.length) {
      return "No Delivery Challan items are selected.";
    }

    for (const item of invoiceItems) {
      const quantity = Number(item.invoice_quantity);
      const available = Number(
        item.available_to_invoice_quantity || 0
      );

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid invoice quantity for ${item.item_description}.`;
      }

      if (quantity > available) {
        return `Invoice quantity cannot exceed available quantity for ${item.item_description}.`;
      }
    }

    for (const charge of charges) {
      if (!charge.charge_type.trim()) {
        return "Please enter the charge type.";
      }

      if (Number(charge.charge_amount) < 0) {
        return "Charge amount cannot be negative.";
      }

      if (Number(charge.gst_rate) < 0) {
        return "GST rate cannot be negative.";
      }
    }

    return "";
  };

  const saveDraft = async () => {
    const validationError = validateItems();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (selectedCustomerNames.length !== 1) {
      setError(
        "All selected Delivery Challans must belong to the same customer."
      );
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        items: invoiceItems.map((item) => ({
          delivery_challan_id: Number(item.delivery_challan_id),
          delivery_challan_item_id: Number(
            item.delivery_challan_item_id
          ),
          invoice_quantity: Number(item.invoice_quantity),
        })),
        charges: charges.map((charge) => ({
          charge_type: charge.charge_type.trim(),
          description: charge.description.trim() || null,
          charge_amount: Number(charge.charge_amount || 0),
          gst_rate: Number(charge.gst_rate || 0),
          remarks: charge.remarks.trim() || null,
        })),
        charge_tax_type: chargeTaxType,
      };

      const response =
        await tradingSalesInvoiceService.createSalesInvoice(payload);

      const body = getResponseData(response);
      const invoice = body.invoice || body;

      if (!invoice?.id) {
        throw new Error(
          "Invoice was saved, but the invoice ID was not returned."
        );
      }

      navigate(`/trading/sales-invoices/${invoice.id}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Create Sales Invoice</h1>
          <div style={styles.subtitle}>
            Create a customer invoice from confirmed Delivery Challans.
          </div>
        </div>

        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => navigate("/trading/sales-invoices")}
        >
          ← Back to Invoice Register
        </button>
      </div>

      {error && <div role="alert" style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,minmax(0,1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <SummaryCard
          label="Selected Delivery Challans"
          value={selectedChallanIds.length}
          color="#6254df"
        />
        <SummaryCard
          label="Selected Invoice Lines"
          value={invoiceItems.length}
          color="#0d91df"
        />
        <SummaryCard
          label="Invoice Quantity"
          value={formatNumber(quantitySummary.invoice)}
          color="#16a36a"
        />
      </div>

      <div style={styles.card}>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {[1, 2].map((number) => (
            <div
              key={number}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                color: step === number ? "#4f46c8" : "#94a3b8",
                fontWeight: 750,
                fontSize: 13,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: step === number ? "#5145cd" : "#e9edf5",
                  color: step === number ? "#fff" : "#64748b",
                }}
              >
                {number}
              </span>
              {number === 1 ? "Select Delivery Challans" : "Invoice Details"}
            </div>
          ))}
        </div>
      </div>

      {step === 1 && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>
            Select Delivery Challans
          </h3>
          <p style={styles.muted}>
            Only issued Delivery Challans with an uninvoiced quantity
            are shown. You can select multiple challans for one invoice.
          </p>
          {/* CUSTOMER SELECTION */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
              marginTop: 18,
              marginBottom: 18,
            }}
          >
            <div>
              <label style={styles.label}>Select Customer *</label>

              <input
                type="text"
                style={styles.input}
                placeholder="Search customer name..."
                value={customerSearch}
                onChange={(e) => searchCustomerList(e.target.value)}
              />

              {loadingCustomers && (
                <p style={styles.muted}>Searching customers...</p>
              )}

              {customers.length > 0 && (
                <div
                  style={{
                    border: "1px solid #d8deea",
                    borderRadius: 7,
                    marginTop: 5,
                    maxHeight: 180,
                    overflowY: "auto",
                    background: "#fff",
                  }}
                >
                  {customers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => {
                        setCustomerSearch(customer.bp_name);
                        setCustomers([]);
                        loadSalesOrders(customer);
                      }}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #edf0f5",
                        fontSize: 13,
                      }}
                    >
                      {customer.bp_name}
                    </div>
                  ))}
                </div>
              )}

              {selectedCustomer && (
                <p style={{ ...styles.muted, marginTop: 6 }}>
                  Selected: <strong>{selectedCustomer.bp_name}</strong>
                </p>
              )}
            </div>

            <div>
              <label style={styles.label}>Select Sales Order *</label>

              <select
                style={styles.input}
                value={selectedSalesOrder?.id || ""}
                disabled={!selectedCustomer || loadingSalesOrders}
                onChange={(e) => {
                  const order = salesOrders.find(
                    (item) => Number(item.id) === Number(e.target.value)
                  );

                  if (order) {
                    selectSalesOrder(order);
                  }
                }}
              >
                <option value="">
                  {!selectedCustomer
                    ? "Select customer first"
                    : loadingSalesOrders
                    ? "Loading Sales Orders..."
                    : "Select Sales Order"}
                </option>

                {salesOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.sales_order_no} — {order.status_code}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ margin: "16px 0" }}>
            <input
              style={styles.input}
              placeholder="Search challan number, customer or Sales Order..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {!selectedSalesOrder ? (
            <p style={styles.muted}>
              Please select a Customer and Sales Order to view available Delivery Challans.
            </p>
          ) : loading ? (
            <p style={styles.muted}>Loading Delivery Challans...</p>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>SELECT</th>
                    <th style={styles.th}>CHALLAN NO.</th>
                    <th style={styles.th}>CHALLAN DATE</th>
                    <th style={styles.th}>CUSTOMER</th>
                    <th style={styles.th}>SALES ORDER</th>
                    <th style={styles.th}>ITEMS</th>
                    <th style={styles.th}>CHALLAN QTY</th>
                    <th style={styles.th}>INVOICED QTY</th>
                    <th style={styles.th}>AVAILABLE QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredChallans.map((row) => {
                    const id = Number(row.delivery_challan_id);
                    const checked = selectedChallanIds.includes(id);

                    return (
                      <tr key={id}>
                        <td style={styles.td}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleChallan(id)}
                          />
                        </td>
                        <td style={styles.td}>
                          <strong>{row.challan_no}</strong>
                        </td>
                        <td style={styles.td}>
                          {row.challan_date
                            ? String(row.challan_date).slice(0, 10)
                            : "-"}
                        </td>
                        <td style={styles.td}>
                          {row.customer_name || "-"}
                        </td>
                        <td style={styles.td}>
                          {row.sales_order_id || "-"}
                        </td>
                        <td style={styles.td}>{row.total_items}</td>
                        <td style={styles.td}>
                          {formatNumber(row.total_challan_quantity)}
                        </td>
                        <td style={styles.td}>
                          {formatNumber(
                            row.total_previously_invoiced_quantity
                          )}
                        </td>
                        <td style={styles.td}>
                          <strong style={{ color: "#059669" }}>
                            {formatNumber(row.total_available_quantity)}
                          </strong>
                        </td>
                      </tr>
                    );
                  })}

                  {!filteredChallans.length && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          ...styles.td,
                          textAlign: "center",
                          padding: 35,
                          color: "#64748b",
                        }}
                      >
                        {challans.length
                          ? "No matching Delivery Challans found."
                          : "No Delivery Challans are currently available for invoicing."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 18,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <span style={styles.muted}>
              {selectedChallanIds.length} Delivery Challan(s) selected
            </span>

            <button
              type="button"
              style={{
                ...styles.primaryButton,
                opacity:
                  !selectedChallanIds.length || loading ? 0.5 : 1,
              }}
              disabled={!selectedChallanIds.length || loading}
              onClick={loadAvailableItems}
            >
              Continue to Invoice Details →
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Selected Challan Summary</h3>
            <p style={styles.muted}>
              Customer: <strong>{selectedCustomerNames[0] || "-"}</strong>
            </p>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>CHALLAN NO.</th>
                    <th style={styles.th}>DATE</th>
                    <th style={styles.th}>SALES ORDER</th>
                    <th style={styles.th}>AVAILABLE QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedChallans.map((row) => (
                    <tr key={row.delivery_challan_id}>
                      <td style={styles.td}>{row.challan_no}</td>
                      <td style={styles.td}>
                        {String(row.challan_date || "").slice(0, 10)}
                      </td>
                      <td style={styles.td}>{row.sales_order_id}</td>
                      <td style={styles.td}>
                        {formatNumber(row.total_available_quantity)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Invoice Items</h3>
            <p style={styles.muted}>
              Enter the quantity to invoice. The quantity cannot exceed
              the remaining available quantity.
            </p>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>CHALLAN NO.</th>
                    <th style={styles.th}>ITEM</th>
                    <th style={styles.th}>CHALLAN QTY</th>
                    <th style={styles.th}>PREVIOUSLY INVOICED</th>
                    <th style={styles.th}>AVAILABLE QTY</th>
                    <th style={styles.th}>INVOICE QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.map((item) => (
                    <tr key={item.delivery_challan_item_id}>
                      <td style={styles.td}>
                        {item.delivery_challan_no}
                      </td>
                      <td style={styles.td}>
                        <strong>{item.item_description || "-"}</strong>
                        <div style={styles.muted}>
                          Item #{item.item_seq_no || "-"}
                        </div>
                      </td>
                      <td style={styles.td}>
                        {formatNumber(item.challan_quantity)}
                      </td>
                      <td style={styles.td}>
                        {formatNumber(
                          item.previously_invoiced_quantity
                        )}
                      </td>
                      <td style={styles.td}>
                        {formatNumber(
                          item.available_to_invoice_quantity
                        )}
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          min="0"
                          max={item.available_to_invoice_quantity}
                          step="0.001"
                          value={item.invoice_quantity}
                          onChange={(e) =>
                            updateQuantity(
                              item.delivery_challan_item_id,
                              e.target.value
                            )
                          }
                          style={{
                            ...styles.input,
                            width: 125,
                          }}
                        />
                      </td>
                    </tr>
                  ))}

                  {!invoiceItems.length && (
                    <tr>
                      <td colSpan={6} style={styles.td}>
                        No invoiceable items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <h3 style={styles.sectionTitle}>Additional Charges</h3>
                <p style={styles.muted}>
                  Add freight or other customer-billable charges.
                </p>
              </div>

              <button
                type="button"
                style={styles.secondaryButton}
                onClick={addCharge}
              >
                + Add Charge
              </button>
            </div>

            <div style={{ marginTop: 14, maxWidth: 300 }}>
              <label style={styles.label}>Charge GST Type</label>
              <select
                style={styles.input}
                value={chargeTaxType}
                onChange={(e) => setChargeTaxType(e.target.value)}
              >
                <option value="NONE">No GST</option>
                <option value="CGST_SGST">CGST + SGST</option>
                <option value="IGST">IGST</option>
              </select>
            </div>

            {charges.map((charge, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "1.1fr 1.5fr 1fr 0.8fr auto",
                  gap: 10,
                  alignItems: "end",
                  padding: "15px 0",
                  borderBottom: "1px solid #edf0f5",
                }}
              >
                <div>
                  <label style={styles.label}>Charge Type</label>
                  <input
                    style={styles.input}
                    value={charge.charge_type}
                    placeholder="Freight"
                    onChange={(e) =>
                      updateCharge(index, "charge_type", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label style={styles.label}>Description</label>
                  <input
                    style={styles.input}
                    value={charge.description}
                    placeholder="Description"
                    onChange={(e) =>
                      updateCharge(index, "description", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label style={styles.label}>Amount</label>
                  <input
                    type="number"
                    min="0"
                    style={styles.input}
                    value={charge.charge_amount}
                    onChange={(e) =>
                      updateCharge(index, "charge_amount", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label style={styles.label}>GST %</label>
                  <input
                    type="number"
                    min="0"
                    style={styles.input}
                    value={charge.gst_rate}
                    onChange={(e) =>
                      updateCharge(index, "gst_rate", e.target.value)
                    }
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeCharge(index)}
                  style={{
                    ...styles.secondaryButton,
                    color: "#be123c",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}

            {!charges.length && (
              <p style={styles.muted}>No additional charges added.</p>
            )}
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Invoice Summary</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,minmax(0,1fr))",
                gap: 14,
              }}
            >
              <SummaryCard
                label="Invoice Lines"
                value={invoiceItems.length}
                color="#6254df"
              />
              <SummaryCard
                label="Total Invoice Quantity"
                value={formatNumber(quantitySummary.invoice)}
                color="#0d91df"
              />
              <SummaryCard
                label="Additional Charges + GST"
                value={formatNumber(
                  chargeSummary.amount + chargeSummary.gst
                )}
                color="#16a36a"
              />
            </div>

            <p style={{ ...styles.muted, marginTop: 14 }}>
              Item selling prices, discounts and GST are taken from the
              source Sales Order by the backend when the draft is saved.
              Final invoice totals will be displayed on the invoice detail
              page.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 30,
            }}
          >
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => setStep(1)}
              disabled={saving}
            >
              ← Back to Challans
            </button>

            <button
              type="button"
              style={{
                ...styles.primaryButton,
                opacity: saving ? 0.6 : 1,
              }}
              disabled={saving || !invoiceItems.length}
              onClick={saveDraft}
            >
              {saving ? "Saving..." : "Save as Draft"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
